// @vendors
import { useState } from 'react'
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
    ChevronRightIcon,
    GlobeAltIcon,
    ArrowLeftStartOnRectangleIcon,
    SpeakerWaveIcon,
    UserGroupIcon,
    SunIcon,
    MoonIcon
} from "@heroicons/react/24/outline";

// @store
import { useLogoutStore } from '../../../store/useLogoutStore';
import { useThemeStore } from '../../../store/useThemeStore';
import { useCompanyStore } from '../../../store/useCompanyStore';

// @components
import ContactUsCard from '../../../components/molecules/ContactUsCard';
import Modal from '../../../components/Modals/Modal';

// @config
import routesConfig from '../../../config/routes.config';

const Settings = () => {
    const { t, i18n } = useTranslation();
    const { handleLogout } = useLogoutStore();
    const { data } = useCompanyStore();
    const { isDarkMode, toggleDarkMode } = useThemeStore();
    const [openModal, setOpenModal] = useState(false);

    const changeLanguage = () => {
        const language = i18n.language === 'en' ? 'es' : 'en';
        i18n.changeLanguage(language);
    };

    const closeModal = () => setOpenModal(!openModal);

    return (
        <div className='w-full h-[80vh] flex flex-col lg:flex-row gap-[20px] overflow-auto'>
            <section className='w-full bg-white dark:bg-dark-cBackground flex flex-col min-h-[50rem] lg:min-h-[80vh] rounded-lg shadow-lg p-[20px] gap-[20px] overflow-auto'>
                <h4 className='flex text-[18px] text-[#03314B] dark:text-dark-cText font-medium mb-[10px]'>
                    {t("professional.settings.settingText")}
                </h4>

                <h6 className='flex text-[15px] text-[#03314B] dark:text-dark-cText font-medium'>
                    {t("professional.settings.generalText")}
                </h6>

                <section className='w-full grid gap-[15px]'>
                    <div
                        className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'
                        onClick={() => changeLanguage()}
                    >
                        <GlobeAltIcon className='w-[25px] h-auto text-[#F97068]' />
                        <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                            {t("professional.settings.language")}
                        </p>
                        <p className='text-[13px] text-[#5C5C5C]/60 dark:text-dark-cText/40'>
                            {i18n.language === 'es' ? 'Español' : 'English'}
                        </p>

                        <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                    </div>

                    <div
                        className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'
                        onClick={closeModal}
                    >
                        <UserGroupIcon className='w-[25px] h-auto text-[#F97068]' />
                        <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                            {t("professional.settings.aboutUs")}
                        </p>
                        <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                    </div>

                    <Link
                        target="_blank"
                        to={{ pathname: routesConfig.termsAndCondition }}
                    >
                        <div className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'>
                            <SpeakerWaveIcon className='w-[25px] h-auto text-[#F97068]' />
                            <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                                {t("professional.settings.terms")}
                            </p>
                            <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                        </div>
                    </Link>

                    <Link
                        target="_blank"
                        to={{ pathname: routesConfig.dataProcessing }}
                    >
                        <div className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'>
                            <SpeakerWaveIcon className='w-[25px] h-auto text-[#F97068]' />
                            <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                                {t("professional.settings.dataProcessing")}
                            </p>
                            <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                        </div>
                    </Link>

                    <div
                        className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'
                        onClick={() => handleLogout(true)}
                    >
                        <ArrowLeftStartOnRectangleIcon className='w-[25px] h-auto text-[#F97068]' />
                        <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                            {t("professional.settings.exit")}
                        </p>
                        <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                    </div>

                    <div
                        className='w-full flex gap-[10px] cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 py-[10px] rounded-md border-b dark:border-white/50'
                        onClick={toggleDarkMode}
                    >
                        {isDarkMode
                            ? <SunIcon className='w-[25px] h-auto text-[#F97068]' />
                            : <MoonIcon className='w-[25px] h-auto text-[#F97068]' />
                        }
                        <p className='w-full text-[13px] text-[#5C5C5C] dark:text-dark-cText/60'>
                            {!isDarkMode ? t("professional.drawer.darkMode") : t("professional.drawer.lightMode")}
                        </p>
                        <ChevronRightIcon className='w-[25px] h-auto text-[#5C5C5C]' />
                    </div>
                </section>

                <ContactUsCard />
            </section>

            <section className='w-full min-h-[30rem] lg:min-h-[80vh] bg-white dark:bg-dark-cBackground flex flex-col rounded-lg shadow-lg p-[20px] gap-[20px] overflow-auto'>
                <h4 className='flex text-[18px] text-[#03314B] dark:text-dark-cText font-medium mb-[10px]'>
                    {t("professional.settings.refered")}
                </h4>

                <div className='w-full grid gap-[20px]'>
                    <p className='w-full text-[13px] text-[#7F7F7F] dark:text-dark-cText/60 '>
                        {t("professional.settings.referedText")}
                    </p>

                    <p className='w-full text-[13px] text-[#7F7F7F] dark:text-dark-cText/60 '>
                        {t("professional.settings.numberOfUserRefered")}
                    </p>
                </div>

                <div className='w-full flex flex-1 items-end'>
                    <button
                        className="w-full mt-4 bg-bgBrand text-textOnBrand py-2 px-6 rounded-lg hover:bg-bgBrandHover disabled:bg-gray-200 transition h-[40px]"
                        disabled={true}
                    >
                        {t("professional.settings.buttonText")}
                    </button>
                </div>
            </section>

            <Modal
                isOpen={openModal}
                onClose={closeModal}
                title=""
                hasButtonClose={false}
            >
                <div className="min-h-[200px] max-w-[600px] flex flex-col items-center gap-[20px]">
                    <h4 className='w-full flex text-[20px] text-[#121A26] font-bold gap-[5px] '>
                        {t("professional.settings.welcomeTo")}
                        <span className='text-[#F97068]'>XERVIX!</span>
                    </h4>
                    <div className="flex flex-col gap-[15px]">
                        <p className='text-[#384860] flex gap-[5px]'>
                            {t("professional.reels.hello")},
                            <span className='font-semibold'>
                                {data?.name ?? ''},
                            </span>
                        </p>
                        <p className='text-[#384860]'>{t("professional.settings.modal.textOne")}</p>
                        <p className='text-[#384860]'>{t("professional.settings.modal.texttwo")}</p>
                        <p className='text-[#384860]'>{t("professional.settings.modal.textThree")}</p>
                        <p className='text-[#384860]'>
                            {t("professional.settings.modal.textFour")}
                            <br />
                            <span className='font-bold'>
                                {t("professional.settings.modal.xervixTeam")}
                            </span>
                        </p>
                        <p className='text-[#384860]/60 text-[14px]'>{t("professional.settings.modal.hasquestion")}</p>
                    </div>
                    <button
                        onClick={closeModal}
                        className="bg-[#F97068] text-white px-4 py-2 rounded-lg w-[200px]"
                    >
                        {t("professional.settings.closeButton")}
                    </button>
                </div>
            </Modal>
        </div>
    )
}

export default Settings